// @ts-nocheck
/**
 * useIncomingCallSession — refactor 2026-06-04 (Kevin big-bang).
 *
 * UN SEUL state machine, UN seul useReducer pour piloter le cycle de vie
 * d'un appel ENTRANT externe (SIP Zadarma -> Twilio -> SDK browser).
 *
 * Remplace les 8 setters eparpilles de connectedContact, les 22 useRef de
 * DialerContext, et les 5 sources de verite concurrentes (cf.
 * docs/refactor/sources-of-truth.md et docs/cartography/inbound-call-lifecycle.md).
 *
 * Fait disparaitre par construction :
 *  - bug A (Raccrocher reste affiche apres prospect raccroche)
 *  - bug B (modale revient apres clic Raccrocher)
 *  - bug F (modale doit rester ouverte en etat 'ended')
 *  - bug 2 ancien (modale fantome 1 min apres)
 *
 * Note : couvre UNIQUEMENT l'inbound externe. L'outbound Power Mode
 * reste pilote par DialerContext + xstate callMachine.ts.
 */

import { createContext, useContext, useReducer, useCallback, useRef, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/api-client';
import * as TwilioCalls from '@/services/dialer/TwilioCallService';
import {
  incomingCallSessionReducer as reducer,
  initialIncomingCallState as initialState,
  type Phase,
  type ProspectInfo,
  type Segment,
} from './incomingCallSessionReducer';

// ── Hook + Context ─────────────────────────────────────────────────────────

interface SessionAPI {
  // Lecture
  phase: Phase;
  callSid: string | null;
  mediaCallSid: string | null;
  parallelCallId: string | null;
  prospect: ProspectInfo | null;
  startedAt: number | null;
  endedAt: number | null;
  lastSegment: Segment | null;
  isRinging: boolean;
  isLive: boolean;
  isEnded: boolean;
  durationSec: number | null;
  // Actions
  handleIncomingReceived: (twilioCall: any) => void;
  handleAccept: () => Promise<void>;
  handleReject: () => void;
  handleHangup: () => Promise<void>;
  handleClose: () => void;
  pushSegment: (seg: Segment) => void;
}

const SessionContext = createContext<SessionAPI | null>(null);

export function IncomingCallSessionProvider({ children, callerData, selectedCustomerId, onAccepted }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const queryClient = useQueryClient();
  // Ref pour la fenetre de stop : pendant 3s apres un HANGUP, on bloque
  // les INCOMING_RECEIVED qui pourraient venir d'evenements en retard.
  const lastHangupAtRef = useRef(0);

  // ── Action : nouvel appel entrant detecte ───────────────────────────────
  const handleIncomingReceived = useCallback((twilioCall: any) => {
    if (!twilioCall) return;
    const cleanProspectPhone = twilioCall.customParameters?.get?.('prospectPhone') || twilioCall.__displayPhone || '';
    const cleanProspectName = twilioCall.customParameters?.get?.('prospectName') || twilioCall.__displayName || '';
    const callSid = twilioCall.parameters?.CallSid || '';
    // i18n-exempt-start: sentinel shared with the server (ParallelCall.ts) and DialerContext's isUsableName
    const isKnown = !!(cleanProspectName && cleanProspectName.trim() && cleanProspectName !== 'Appelant inconnu');
    const prospect: ProspectInfo = {
      name: isKnown ? cleanProspectName : 'Appelant inconnu',
    // i18n-exempt-end
      phone: cleanProspectPhone,
      company: '',
      isKnown,
    };

    const mediaCallSid = twilioCall.customParameters?.get?.('inboundCallSid') || callSid;
    // 2026-09-27, /voice-handler cree deja le ParallelCall des la sonnerie
    // (cf. TwilioService.receiveInboundCall) ; son id voyage jusqu'ici en
    // Parameter Twilio. Present : handleAccept met a jour cette ligne au
    // lieu d'en creer une seconde.
    const parallelCallId = twilioCall.customParameters?.get?.('parallelCallId') || twilioCall.__parallelCallId || '';
    dispatch({ type: 'INCOMING_RECEIVED', twilioCall, callSid, mediaCallSid, parallelCallId, prospect });

    // Listeners SDK Twilio
    const onDisconnect = () => dispatch({ type: 'HANGUP', reason: 'remote', callSid });
    const onCancel = () => dispatch({ type: 'HANGUP', reason: 'cancel', callSid });
    const onError = () => dispatch({ type: 'HANGUP', reason: 'error', callSid });
    const onAccept = () => dispatch({ type: 'BRIDGED', callSid });
    twilioCall.on('disconnect', onDisconnect);
    twilioCall.on('cancel', onCancel);
    twilioCall.on('error', onError);
    twilioCall.on('accept', onAccept);
  }, []);

  // ── Action : agent decroche ─────────────────────────────────────────────
  const handleAccept = useCallback(async () => {
    if (state.phase !== 'ringing' || !state.twilioCall || !state.prospect) return;
    dispatch({ type: 'ACCEPT_START' });
    const nowIso = new Date().toISOString();
    // 1. Rattache l'agent au ParallelCall. Depuis le 2026-09-27, /voice-handler
    //    (cote serveur) a deja cree cette ligne des la sonnerie, y compris
    //    pour un appel jamais decroche, ce que la creation ici ne pouvait pas
    //    couvrir. Si son id nous est parvenu (Parameter Twilio), on la MET A
    //    JOUR ; sinon (vieux binaire serveur, ou echec de creation cote
    //    /voice-handler) on retombe sur l'ancien comportement pour ne jamais
    //    perdre l'appel decroche.
    try {
      let parallelCallId = state.parallelCallId || '';
      if (parallelCallId) {
        await api.entities.ParallelCall.update(parallelCallId, {
          call_status: 'connected_to_agent',
          agent_email: callerData?.caller_email,
          agent_name: callerData?.caller_name,
          connected_at: nowIso,
        });
      } else {
        const created: any = await api.entities.ParallelCall.create({
          customer_id: selectedCustomerId || '',
          prospect_name: state.prospect.name,
          prospect_phone: state.prospect.phone,
          prospect_company: state.prospect.company,
          call_status: 'connected_to_agent',
          agent_email: callerData?.caller_email,
          agent_name: callerData?.caller_name,
          started_at: nowIso,
          connected_at: nowIso,
          // The parent call's sid, not this browser leg's: the live transcript,
          // the coaching data and the recording are all keyed by the parent
          // call, and a leg sid here left the AI coach empty and saved nothing.
          external_call_id: state.mediaCallSid || state.callSid,
        });
        parallelCallId = created?.id || '';
      }
      dispatch({ type: 'ACCEPT_SUCCESS', parallelCallId });
      queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      // 2. Accept Twilio (ouvre le bridge media)
      state.twilioCall.accept();
      // 3. Notifie le parent (DialerContext) pour qu'il puisse ouvrir
      //    la modale ProspectDetailPanel sur ce prospet.
      if (typeof onAccepted === 'function') {
        try {
          onAccepted({
            parallelCallId,
            prospect: state.prospect,
            callSid: state.callSid,
          });
        } catch (_e) { /* silent */ }
      }
    } catch (e: any) {
      console.error('[IncomingCallSession] accept failed', e?.message);
      dispatch({ type: 'HANGUP', reason: 'error' });
    }
  }, [state.phase, state.twilioCall, state.prospect, state.callSid, state.mediaCallSid, state.parallelCallId, selectedCustomerId, callerData, queryClient, onAccepted]);

  // ── Action : agent decline ──────────────────────────────────────────────
  const handleReject = useCallback(() => {
    if (state.phase !== 'ringing' || !state.twilioCall) return;
    try { state.twilioCall.reject(); } catch (_e) { /* silent */ }
    dispatch({ type: 'REJECTED' });
  }, [state.phase, state.twilioCall]);

  // ── Action : agent raccroche (depuis l'UI) ──────────────────────────────
  const handleHangup = useCallback(async () => {
    if (state.phase !== 'live' && state.phase !== 'accepted') return;
    lastHangupAtRef.current = Date.now();
    // Optimiste : on switche tout de suite en 'ended'
    dispatch({ type: 'HANGUP', reason: 'local' });

    // Cleanup complet : disconnect browser + hangup serveur (couvre leg
    // child + leg parent SIP). Centralise dans TwilioCallService.
    try { state.twilioCall?.disconnect(); } catch (_e) { /* silent */ }
    await TwilioCalls.hangupAllMine();
    // Update ParallelCall MongoDB en 'completed'
    if (state.parallelCallId) {
      try {
        await api.entities.ParallelCall.update(state.parallelCallId, {
          call_status: 'completed',
          ended_at: new Date().toISOString(),
        });
        queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      } catch (e: any) {
        console.warn('[IncomingCallSession] ParallelCall.update failed', e?.message);
      }
    }
  }, [state.phase, state.twilioCall, state.parallelCallId, queryClient]);

  // ── Action : agent ferme la modale (post-qualif) ────────────────────────
  const handleClose = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // ── Push transcript segment ─────────────────────────────────────────────
  const pushSegment = useCallback((seg: Segment) => {
    dispatch({ type: 'SEGMENT', segment: seg });
  }, []);

  // ── Side effect : si l'appel s'est termine cote SDK Twilio (HANGUP via
  //    'disconnect' event), on doit AUSSI marquer le ParallelCall completed
  //    et appeler hangup-all-mine pour fermer la leg SIP cote serveur.
  // ────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (state.phase !== 'ended') return;
    // Skip si le hangup a deja ete declenche par handleHangup (lastHangupAtRef)
    if (Date.now() - lastHangupAtRef.current < 500) return;

    // 2026-09-27, state.parallelCallId est desormais pose des la sonnerie
    // (server-side /voice-handler cree la ligne AVANT l'acceptation), donc il
    // est present pour TOUS les devices fanned-out, pas seulement celui qui
    // decroche. state.startedAt, lui, n'est pose que par ACCEPT_START/BRIDGED,
    // donc uniquement sur LE device qui a reellement pris l'appel. Sans ce
    // second garde-fou, un device juste annule (l'agent a decroche ailleurs)
    // marquerait la ligne 'completed' et raccrocherait l'appel en cours,
    // exactement le bug #H que le commentaire ci-dessous decrivait avant
    // cette regle serveur.
    const thisDeviceAnswered = !!state.parallelCallId && !!state.startedAt;

    // Cas : prospect a raccroche cote tel (HANGUP_REMOTE / cancel / error)
    if (thisDeviceAnswered) {
      api.entities.ParallelCall.update(state.parallelCallId as string, {
        call_status: 'completed',
        ended_at: new Date().toISOString(),
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
      }).catch((e: any) => {
        console.warn('[IncomingCallSession] post-disconnect cleanup failed', e?.message);
      });
    }
    // Server-side hangup of every call of the agent, for a SIP leg left
    // behind (bug #H). Only when THIS session took the call: an incoming call
    // rings every device of the agent, and when one answers Twilio cancels the
    // others. A cancelled device reaching here used to hang up the call just
    // answered elsewhere (measured 2026-09-27: answered, then cut within 1 s).
    if (thisDeviceAnswered) TwilioCalls.hangupAllMine();
  }, [state.phase, state.parallelCallId, state.startedAt, queryClient]);

  // ── Derives ─────────────────────────────────────────────────────────────
  const durationSec = useMemo(() => {
    if (!state.startedAt) return null;
    const end = state.endedAt || Date.now();
    return Math.max(0, Math.floor((end - state.startedAt) / 1000));
  }, [state.startedAt, state.endedAt]);

  const value: SessionAPI = useMemo(() => ({
    phase: state.phase,
    callSid: state.callSid,
    mediaCallSid: state.mediaCallSid,
    parallelCallId: state.parallelCallId,
    prospect: state.prospect,
    startedAt: state.startedAt,
    endedAt: state.endedAt,
    lastSegment: state.lastSegment,
    isRinging: state.phase === 'ringing',
    isLive: state.phase === 'live' || state.phase === 'accepted',
    isEnded: state.phase === 'ended',
    durationSec,
    handleIncomingReceived,
    handleAccept,
    handleReject,
    handleHangup,
    handleClose,
    pushSegment,
  }), [state, durationSec, handleIncomingReceived, handleAccept, handleReject, handleHangup, handleClose, pushSegment]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useIncomingCallSession(): SessionAPI {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useIncomingCallSession must be used within IncomingCallSessionProvider');
  }
  return ctx;
}
